import { useState, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ChevronLeft, ArrowRight, Target, Check } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import { OnboardingLayout, INVESTOR_ONBOARDING_STEPS } from '@/components/OnboardingLayout';
import { progressFor } from '@/lib/onboarding/machine';
import { useAuth } from '@/lib/supabase/auth';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { handleError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';
import { track } from '@/lib/analytics';
import { useEntrance } from '@/lib/a11y/reduced-motion';

const SECTORS = [
  'SaaS / Software', 'Fintech', 'Healthcare', 'AI / ML', 'Consumer',
  'Enterprise', 'CleanTech', 'E-commerce', 'Deeptech', 'Marketplace',
];

const STAGES = [
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series-a', label: 'Series A' },
  { value: 'series-b', label: 'Series B+' },
];

const GEOGRAPHIES = [
  'Switzerland', 'DACH', 'Europe', 'UK', 'US', 'MENA', 'Asia', 'Global',
];

const CURRENCIES = ['CHF', 'USD', 'EUR'];

const LEAD_OPTIONS = [
  { value: 'lead' as const, label: 'Lead' },
  { value: 'follow' as const, label: 'Follow' },
  { value: 'both' as const, label: 'Both' },
];

/** Parse a currency text field to a positive integer, or null if empty/invalid. */
function parseAmount(raw: string): number | null {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return null;
  const n = parseInt(digits, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function InvestorThesisScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const entrance = useEntrance();
  const { isDesktop } = useResponsive();
  const { show } = useAlert();

  const user = useProfileStore((s) => s.user);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);
  const { user: authUser, refreshProfile } = useAuth();

  const [sectors, setSectors] = useState<string[]>(investorProfile?.investmentFocus || []);
  const [stages, setStages] = useState<string[]>(investorProfile?.stagePreference || []);
  const [geographies, setGeographies] = useState<string[]>(investorProfile?.geographies || []);
  // NO default ticket — unknown stays unknown (empty).
  const [currency, setCurrency] = useState(investorProfile?.ticketCurrency || 'CHF');
  const [ticketMin, setTicketMin] = useState(
    investorProfile?.ticketSize?.min ? String(investorProfile.ticketSize.min) : '',
  );
  const [ticketMax, setTicketMax] = useState(
    investorProfile?.ticketSize?.max ? String(investorProfile.ticketSize.max) : '',
  );
  const [leadPreference, setLeadPreference] = useState<'lead' | 'follow' | 'both' | null>(
    investorProfile?.leadPreference ?? null,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const toggle = (list: string[], setList: (v: string[]) => void, value: string, errorKey: string) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    if (errors[errorKey]) setErrors((e) => ({ ...e, [errorKey]: '' }));
  };

  const minValue = useMemo(() => parseAmount(ticketMin), [ticketMin]);
  const maxValue = useMemo(() => parseAmount(ticketMax), [ticketMax]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (sectors.length === 0) next.sectors = 'Select at least one sector';
    if (stages.length === 0) next.stages = 'Select at least one stage';
    if (geographies.length === 0) next.geographies = 'Select at least one geography';
    if (minValue == null) next.ticket = 'Enter a minimum ticket';
    else if (maxValue == null) next.ticket = 'Enter a maximum ticket';
    else if (maxValue < minValue) next.ticket = 'Maximum must be at least the minimum';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleContinue = async () => {
    if (isSaving) return;
    if (!validate() || minValue == null || maxValue == null) return;

    setSaveError(null);
    setIsSaving(true);

    setInvestorProfile?.({
      id: investorProfile?.id || `investor-${Date.now()}`,
      firm: investorProfile?.firm,
      investorType: investorProfile?.investorType,
      role: investorProfile?.role,
      location: investorProfile?.location || '',
      investmentFocus: sectors,
      stagePreference: stages,
      geographies,
      ticketSize: { min: minValue, max: maxValue },
      ticketCurrency: currency,
      leadPreference: leadPreference ?? undefined,
      portfolioCount: investorProfile?.portfolioCount || 0,
      linkedInVerified: investorProfile?.linkedInVerified || false,
      investmentHistory: investorProfile?.investmentHistory || [],
      portfolioClaims: investorProfile?.portfolioClaims || [],
    });

    if (isSupabaseConfigured() && authUser?.id) {
      try {
        const payload = {
          user_id: authUser.id,
          name: user?.name || authUser.email || 'Investor',
          investment_focus: sectors,
          stage_preference: stages,
          geographies,
          ticket_size_min: minValue,
          ticket_size_max: maxValue,
          // Both of these were set on the store two dozen lines above and
          // omitted here, so the currency the investor picked and their
          // lead/follow preference were lost at the end of onboarding. They
          // had no columns to go to until 20260731140000.
          ticket_currency: currency,
          lead_preference: leadPreference ?? null,
        };
        const { error } = await supabase
          .from('investor_profiles')
          .upsert(payload, { onConflict: 'user_id' });
        if (error) throw error;

        await refreshProfile();
      } catch (err) {
        setIsSaving(false);
        const { message, isCancelled } = handleError(err, { context: 'InvestorThesis.handleContinue' });
        if (!isCancelled) {
          setSaveError(message);
          show({ title: "Couldn't save your thesis", message, buttons: [{ text: 'OK' }] });
        }
        return;
      }
    }

    // Activation §8 — thesis captured, after the awaited investor_profiles
    // upsert above (a throw skips to catch, never here).
    track('thesis_done', { sectors: sectors.length, stages: stages.length });

    setIsSaving(false);
    router.push('/(onboarding)/complete');
  };

  const Chip = ({ label, selected, onPress, testKey }: { label: string; selected: boolean; onPress: () => void; testKey: string }) => (
    <View key={testKey} style={{ borderRadius: 999, overflow: 'hidden' }}>
      <Pressable
        onPress={onPress}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: selected }}
        accessibilityLabel={label}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          minHeight: 40,
          paddingHorizontal: 14,
          paddingVertical: 9,
          borderRadius: 999,
          backgroundColor: selected ? colors.primary[600] : colors.surface.primary,
          borderWidth: 1,
          borderColor: selected ? colors.primary[600] : colors.border.light,
        }}
      >
        {selected && <Check size={14} color={colors.text.inverse} />}
        <Text style={{ color: selected ? colors.text.inverse : colors.text.primary, fontSize: 13, fontWeight: '500' }}>
          {label}
        </Text>
      </Pressable>
    </View>
  );

  const amountInputStyle = {
    backgroundColor: colors.surface.primary,
    borderWidth: 1,
    borderColor: errors.ticket ? colors.red.main : colors.border.light,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 14,
    color: colors.text.primary,
    fontSize: 16,
  };

  const formContent = (
    <>
      <View className={isWeb && isDesktop ? 'gap-6' : 'gap-5'}>
        {/* Sectors */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Sectors <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {SECTORS.map((s) => (
              <Chip key={`sector-${s}`} testKey={`sector-${s}`} label={s} selected={sectors.includes(s)} onPress={() => toggle(sectors, setSectors, s, 'sectors')} />
            ))}
          </View>
          {!!errors.sectors && <Text style={{ color: colors.red.main }} className="text-xs mt-1">{errors.sectors}</Text>}
        </View>

        {/* Stages */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Stages <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {STAGES.map((s) => (
              <Chip key={`stage-${s.value}`} testKey={`stage-${s.value}`} label={s.label} selected={stages.includes(s.value)} onPress={() => toggle(stages, setStages, s.value, 'stages')} />
            ))}
          </View>
          {!!errors.stages && <Text style={{ color: colors.red.main }} className="text-xs mt-1">{errors.stages}</Text>}
        </View>

        {/* Geography */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Geography <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {GEOGRAPHIES.map((g) => (
              <Chip key={`geo-${g}`} testKey={`geo-${g}`} label={g} selected={geographies.includes(g)} onPress={() => toggle(geographies, setGeographies, g, 'geographies')} />
            ))}
          </View>
          {!!errors.geographies && <Text style={{ color: colors.red.main }} className="text-xs mt-1">{errors.geographies}</Text>}
        </View>

        {/* Ticket size */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Ticket size <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          {/* Currency selector */}
          <View className="flex-row gap-2 mb-2">
            {CURRENCIES.map((c) => {
              const selected = currency === c;
              return (
                <View key={`cur-${c}`} style={{ borderRadius: 10, overflow: 'hidden' }}>
                  <Pressable
                    onPress={() => setCurrency(c)}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    accessibilityLabel={`Currency ${c}`}
                    style={{
                      minHeight: 40,
                      paddingHorizontal: 16,
                      justifyContent: 'center',
                      borderRadius: 10,
                      backgroundColor: selected ? colors.primary[600] : colors.surface.primary,
                      borderWidth: 1,
                      borderColor: selected ? colors.primary[600] : colors.border.light,
                    }}
                  >
                    <Text style={{ color: selected ? colors.text.inverse : colors.text.primary, fontWeight: '600', fontSize: 13 }}>{c}</Text>
                  </Pressable>
                </View>
              );
            })}
          </View>
          <View className="flex-row items-center gap-3">
            <View className="flex-1">
              <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">Minimum</Text>
              <TextInput
                style={amountInputStyle}
                placeholder="e.g., 25000"
                placeholderTextColor={colors.text.tertiary}
                value={ticketMin}
                onChangeText={(t) => { setTicketMin(t); if (errors.ticket) setErrors((e) => ({ ...e, ticket: '' })); }}
                keyboardType="number-pad"
                accessibilityLabel={`Minimum ticket in ${currency}`}
              />
            </View>
            <Text style={{ color: colors.text.tertiary }} className="mt-5">–</Text>
            <View className="flex-1">
              <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">Maximum</Text>
              <TextInput
                style={amountInputStyle}
                placeholder="e.g., 250000"
                placeholderTextColor={colors.text.tertiary}
                value={ticketMax}
                onChangeText={(t) => { setTicketMax(t); if (errors.ticket) setErrors((e) => ({ ...e, ticket: '' })); }}
                keyboardType="number-pad"
                accessibilityLabel={`Maximum ticket in ${currency}`}
              />
            </View>
          </View>
          {!!errors.ticket && <Text style={{ color: colors.red.main }} className="text-xs mt-1">{errors.ticket}</Text>}
        </View>

        {/* Lead / follow (optional) */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">Lead or follow</Text>
          <View className="flex-row gap-2">
            {LEAD_OPTIONS.map((o) => {
              const selected = leadPreference === o.value;
              return (
                <View key={`lead-${o.value}`} className="flex-1" style={{ borderRadius: 12, overflow: 'hidden' }}>
                  <Pressable
                    onPress={() => setLeadPreference(selected ? null : o.value)}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    accessibilityLabel={o.label}
                    style={{
                      minHeight: 44,
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 12,
                      backgroundColor: selected ? colors.primary[50] : colors.surface.primary,
                      borderWidth: 1.5,
                      borderColor: selected ? colors.primary[500] : colors.border.light,
                    }}
                  >
                    <Text style={{ color: selected ? colors.primary[700] : colors.text.primary, fontWeight: '500' }}>{o.label}</Text>
                  </Pressable>
                </View>
              );
            })}
          </View>
        </View>
      </View>

      {!!saveError && (
        <View className="mt-4 p-3 rounded-xl" style={{ backgroundColor: colors.red.light, borderWidth: 1, borderColor: colors.red.main }}>
          <Text style={{ color: colors.red.main }} className="text-sm">{saveError}</Text>
        </View>
      )}

      <View className="mt-8">
        <View style={{ borderRadius: 12, overflow: 'hidden', backgroundColor: isSaving ? colors.border.medium : colors.primary[600] }}>
          <Pressable
            onPress={handleContinue}
            disabled={isSaving}
            accessibilityRole="button"
            accessibilityLabel="Finish and view dealflow"
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 16 }}
          >
            <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
              {isSaving ? 'Saving…' : 'Continue'}
            </Text>
            {!isSaving && <ArrowRight size={20} color={colors.text.inverse} />}
          </Pressable>
        </View>
      </View>
    </>
  );

  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        currentStep="thesis"
        steps={INVESTOR_ONBOARDING_STEPS}
        title="Investment Thesis"
        subtitle="What you invest in — this powers your dealflow matches"
        userType="investor"
        entityName={user?.name || undefined}
      >
        {formContent}
      </OnboardingLayout>
    );
  }

  const { index, total } = progressFor('investor', 'thesis');

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} className="flex-1">
          <Animated.View entering={entrance(FadeInDown.delay(100).duration(500))} className="px-5 pt-4 pb-4">
            <View className="flex-row items-center justify-between">
              <Pressable
                onPress={() => router.back()}
                accessibilityRole="button"
                accessibilityLabel="Go back"
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <View className="flex-row gap-2">
                {Array.from({ length: total }).map((_, i) => (
                  <View
                    key={`dot-${i}`}
                    className="h-1 w-8 rounded-full"
                    style={{ backgroundColor: i <= index ? colors.primary[500] : colors.border.light }}
                  />
                ))}
              </View>
              <View className="w-10" />
            </View>
          </Animated.View>

          <Animated.View entering={entrance(FadeInDown.delay(200).duration(500))} className="px-5 pb-4">
            <View className="flex-row items-center gap-3 mb-2">
              <Target size={22} color={colors.primary[600]} />
              <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                Step {index + 1} of {total}
              </Text>
            </View>
            <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">Investment Thesis</Text>
            <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
              What you invest in — this powers your dealflow matches
            </Text>
          </Animated.View>

          <ScrollView className="flex-1 px-5" showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <Animated.View entering={entrance(FadeInUp.delay(300).duration(500))} className="pb-10">
              {formContent}
            </Animated.View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}
